import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import Button from '@atlaskit/button/new';
import Link from '@atlaskit/link';
import { cssMap } from '@atlaskit/css';
import { Box, Inline } from '@atlaskit/primitives/compiled';

const LEARN_MORE_HREF =
	'https://support.atlassian.com/confluence-cloud/docs/create-and-use-synced-blocks/#Manage-synced-blocks';

const i18n = defineMessages({
	title: {
		id: 'content-markdown-mode.unlink-synced-blocks-dialog.title',
		description:
			'Title of the modal shown when the user tries to convert a page to markdown but the page contains synced blocks. The dialog asks them to unlink (unsync) the blocks first.',
		defaultMessage: 'Unlink synced blocks to convert',
	},
	body: {
		id: 'content-markdown-mode.unlink-synced-blocks-dialog.body',
		description:
			'Body copy of the unlink-synced-blocks dialog. {count} is the number of synced blocks in the document (always >= 1 in practice; 0 takes the plural branch defensively).',
		defaultMessage:
			'{count, plural, one {This content contains 1 synced block syncing to multiple locations. Converting to markdown will detach the synced block and result in it being removed from multiple synced locations, where it will appear as an "Unsynced block". Unsync it to continue.} other {This content contains {count} synced blocks syncing to multiple locations. Converting to markdown will detach the synced blocks and result in them being removed from multiple synced locations, where they will appear as "Unsynced blocks". Unsync them to continue.}}',
	},
	learnMore: {
		id: 'content-markdown-mode.unlink-synced-blocks-dialog.learn-more',
		description:
			'Footer link in the unlink-synced-blocks dialog that opens external documentation about managing synced blocks.',
		defaultMessage: 'Learn more',
	},
	cancel: {
		id: 'content-markdown-mode.unlink-synced-blocks-dialog.cancel',
		description: 'Cancel button label in the unlink-synced-blocks dialog.',
		defaultMessage: 'Cancel',
	},
	understood: {
		id: 'content-markdown-mode.unlink-synced-blocks-dialog.understood',
		description:
			'Primary acknowledgement button label in the unlink-synced-blocks dialog. Dismissing the dialog — there is no "convert anyway" action.',
		defaultMessage: 'Understood',
	},
});

const styles = cssMap({
	footer: {
		width: '100%',
	},
});

export type UnlinkSyncedBlocksDialogProps = {
	/** Number of `bodiedSyncBlock` nodes in the document. Drives the pluralised body copy. */
	syncBlockCount: number;
	/** Called when the user dismisses the dialog (close icon, escape, blanket, Cancel, or Understood). */
	onClose: () => void;
};

const UnlinkSyncedBlocksDialogContents = ({
	syncBlockCount,
	onClose,
}: UnlinkSyncedBlocksDialogProps) => (
	<>
		<ModalHeader hasCloseButton>
			<ModalTitle>
				<FormattedMessage {...i18n.title} />
			</ModalTitle>
		</ModalHeader>
		<ModalBody>
			<FormattedMessage {...i18n.body} values={{ count: syncBlockCount }} />
		</ModalBody>
		<ModalFooter>
			<Box xcss={styles.footer}>
				<Inline alignBlock="center" spread="space-between">
					<Link
						href={LEARN_MORE_HREF}
						target="_blank"
						rel="noopener noreferrer"
						testId="unlink-synced-blocks-dialog-learn-more"
					>
						<FormattedMessage {...i18n.learnMore} />
					</Link>
					<Inline space="space.100">
						<Button
							appearance="subtle"
							onClick={onClose}
							testId="unlink-synced-blocks-dialog-cancel"
						>
							<FormattedMessage {...i18n.cancel} />
						</Button>
						<Button
							appearance="warning"
							onClick={onClose}
							testId="unlink-synced-blocks-dialog-understood"
						>
							<FormattedMessage {...i18n.understood} />
						</Button>
					</Inline>
				</Inline>
			</Box>
		</ModalFooter>
	</>
);

export type UnlinkSyncedBlocksDialogWrapperProps = UnlinkSyncedBlocksDialogProps & {
	isOpen: boolean;
};

/**
 * Informational blocker shown when the user tries to convert a page to
 * markdown but the document contains one or more `bodiedSyncBlock` nodes.
 *
 * Synced blocks have no markdown representation and they fan out to other
 * pages, so we don't expose a "convert anyway" action — the user must
 * unlink them in the editor first. The only outcomes here are dismiss
 * (Close / Cancel / Understood) and "Learn more" (external docs).
 */
export const UnlinkSyncedBlocksDialog = ({
	isOpen,
	syncBlockCount,
	onClose,
}: UnlinkSyncedBlocksDialogWrapperProps) => (
	<ModalTransition>
		{isOpen ? (
			<Modal onClose={onClose} width="medium" testId="unlink-synced-blocks-dialog">
				<UnlinkSyncedBlocksDialogContents syncBlockCount={syncBlockCount} onClose={onClose} />
			</Modal>
		) : null}
	</ModalTransition>
);
